import { Children, cloneElement, forwardRef, type JSX, type ReactElement } from 'react';
import { List, type RowComponentProps } from 'react-window';

import { AutoComplete } from './AutoComplete';
import { SelectAllDeselectAll } from './SelectAllDeselectAll';
import type { AutocompleteProps } from './types';
import { useSelectAllDeselectAll } from './useSelectAllDeselectAll';

const LISTBOX_PADDING = 8;
const ITEM_SIZE = 36;

type ListboxRowProps = {
  itemData: React.ReactElement<Record<string, unknown>>[];
};

function ListboxRow({ index, style, itemData }: RowComponentProps<ListboxRowProps>): ReactElement {
  const match = typeof style.transform === 'string' ? style.transform.match(/translateY\((-?[\d.]+)px\)/) : null;
  const currentY = match ? Number(match[1]) : 0;

  return cloneElement(itemData[index], {
    style: {
      ...style,
      transform: `translateY(${currentY + LISTBOX_PADDING}px)`,
    },
  });
}

type ListboxOption = string | number | { id: string; name: string; [key: PropertyKey]: unknown };

type ListboxComponentPropsBase = {
  children?: React.ReactNode;
  width?: string;
  itemSize?: number;
  options?: readonly ListboxOption[];
  currentValue?: ListboxOption | ListboxOption[];
};

type ListboxComponentPropsWithSelectAll = ListboxComponentPropsBase & {
  showSelectAllOptions: true;
  onSelectAll: (event: React.MouseEvent<HTMLButtonElement>) => void;
  onDeselectAll: (event: React.MouseEvent<HTMLButtonElement>) => void;
};

type ListboxComponentPropsWithoutSelectAll = ListboxComponentPropsBase & {
  showSelectAllOptions?: false;
  onSelectAll?: never;
  onDeselectAll?: never;
};

type ListboxComponentProps = ListboxComponentPropsWithSelectAll | ListboxComponentPropsWithoutSelectAll;

export const ListboxComponent = forwardRef<HTMLDivElement, ListboxComponentProps>(
  (
    {
      children,
      width,
      itemSize = ITEM_SIZE,
      showSelectAllOptions = false,
      options = [],
      currentValue,
      onSelectAll,
      onDeselectAll,
      ...other
    },
    ref
  ) => {
    const itemData = Children.toArray(children) as React.ReactElement<Record<string, unknown>>[];
    const itemCount = itemData.length;

    const calculateSelectedCount = (): number => {
      if (Array.isArray(currentValue)) {
        return currentValue.length;
      }
      return currentValue ? 1 : 0;
    };

    const getHeight = (): number => {
      const baseHeight = itemCount * itemSize;
      const clampedHeight = baseHeight > 8 * itemSize ? 8 * itemSize : baseHeight;

      return clampedHeight + 2 * LISTBOX_PADDING;
    };

    const selectedCount = calculateSelectedCount();

    return (
      <div ref={ref} data-test="list-of-items">
        {showSelectAllOptions && onSelectAll && onDeselectAll && (
          <SelectAllDeselectAll
            totalOptions={options.length}
            selectedCount={selectedCount}
            onSelectAll={onSelectAll}
            onDeselectAll={onDeselectAll}
          />
        )}
        <List
          {...other}
          rowComponent={ListboxRow}
          rowCount={itemCount}
          rowHeight={itemSize}
          rowProps={{ itemData }}
          overscanCount={5}
          style={{ height: getHeight(), width: width || '100%' }}
          tagName="ul"
        />
      </div>
    );
  }
);

export function AutoCompleteVirtualize<
  T,
  Multiple extends boolean | undefined = undefined,
  DisableClearable extends boolean | undefined = undefined,
  FreeSolo extends boolean | undefined = undefined,
>({ itemSize, slotProps, ...props }: AutocompleteProps<T, Multiple, DisableClearable, FreeSolo>): JSX.Element {
  const listboxProps = useSelectAllDeselectAll({
    handleChange: props.onChange,
    options: props.options,
    currentValue: props.value,
    showSelectAllOptions: props.showSelectAllOptions,
  });

  return (
    <AutoComplete
      disableListWrap
      slotProps={{
        ...slotProps,
        listbox: {
          component: ListboxComponent,
          ...listboxProps,
          ...(itemSize !== undefined ? { itemSize } : {}),
          ...slotProps?.listbox,
        },
      }}
      {...props}
    />
  );
}
